import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import InvoiceForm from "../../components/InvoiceForm/InvoiceForm";
import invoiceService from "../../services/invoice.service";
import { PurchaseInvoice } from "../../types/invoice.types";
import { useAuth } from "../../../auth/hooks/useAuth";
import { getBusinessId } from "../../services/invoice.service";

const InvoiceFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const grnId = searchParams.get("grn_id");

  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState<PurchaseInvoice | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = Boolean(invoiceId && invoiceId !== "new");

  const getBusinessIdForPage = () => getBusinessId(user);

  // Load existing invoice for edit
  useEffect(() => {
    if (isEditMode && invoiceId) {
      loadInvoice(invoiceId);
    }
  }, [isEditMode, invoiceId]);

  const loadInvoice = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceService.getInvoice({
        business_id: getBusinessIdForPage(),
        invoice_id: id,
      });
      if (response.success) {
        setInvoice(response.data);
      } else {
        setError("Failed to load invoice");
      }
    } catch (error: any) {
      console.error("Error loading invoice:", error);
      setError(error.message || "Failed to load invoice");
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (createdInvoice: PurchaseInvoice) => {
    toast.success(
      isEditMode
        ? "Invoice updated successfully!"
        : "Invoice created successfully!"
    );
    navigate(`/business/procurement/invoices/${createdInvoice.invoice_id}`);
  };

  const handleCancel = () => {
    navigate("/business/procurement/invoices");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <InvoiceForm
      invoiceId={isEditMode ? invoiceId : undefined}
      grnId={grnId || undefined}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
    />
  );
};

export default InvoiceFormPage;
